import { useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import { Card } from "@/components/ui/card";
import { useI18n } from "../../i18n/i18n";

interface PieChartData {
  labels: string[];
  values: number[];
}

interface BarChartData {
  labels: string[];
  values: number[];
}

interface SalaryChartsProps {
  pieChartData: PieChartData;
  barChartData: BarChartData;
  currency: string; // Added currency prop
}

const SalaryCharts = ({
  pieChartData,
  barChartData,
  currency,
}: SalaryChartsProps) => {
  const pieChartRef = useRef<HTMLCanvasElement | null>(null);
  const barChartRef = useRef<HTMLCanvasElement | null>(null);
  const pieChartInstance = useRef<Chart | null>(null);
  const barChartInstance = useRef<Chart | null>(null);
  const { t } = useI18n();

  useEffect(() => {
    if (!pieChartRef.current || !barChartRef.current) return;

    // Destroy previous chart instances if they exist
    if (pieChartInstance.current) {
      pieChartInstance.current.destroy();
    }
    if (barChartInstance.current) {
      barChartInstance.current.destroy();
    }

    // Create Pie Chart
    const pieCtx = pieChartRef.current.getContext("2d");
    if (pieCtx) {
      pieChartInstance.current = new Chart(pieCtx, {
        type: "doughnut",
        data: {
          labels: pieChartData.labels,
          datasets: [
            {
              data: pieChartData.values,
              backgroundColor: [
                "#EA580C", // Primary (Take-Home Pay)
                "#6B7280", // Medium Gray (Taxes)
                "#22C55E", // Green (Retirement)
                "#3B82F6", // Blue (Health Insurance)
                "#D1D5DB", // Light Gray (Other)
              ],
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  let label = context.label || "";
                  if (label) {
                    label += ": ";
                  }
                  if (context.parsed !== null) {
                    label += new Intl.NumberFormat(
                      currency === "USD" ? "en-US" : "de-DE",
                      {
                        // Updated currency formatting
                        style: "currency",
                        currency: currency,
                      },
                    ).format(context.parsed);
                  }
                  return label;
                },
              },
            },
            legend: { position: "bottom" },
          },
        },
      });
    }

    // Create Bar Chart
    const barCtx = barChartRef.current.getContext("2d");
    if (barCtx) {
      barChartInstance.current = new Chart(barCtx, {
        type: "bar",
        data: {
          labels: barChartData.labels,
          datasets: [
            {
              label: t("tools.salary.annualIncome"),
              data: barChartData.values,
              backgroundColor: ["#6B7280", "#EA580C"], // Gray for Gross, Primary for Net
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: function (context) {
                  let label = context.dataset.label || "";
                  if (label) {
                    label += ": ";
                  }
                  if (context.parsed.y !== null) {
                    label += new Intl.NumberFormat(
                      currency === "USD" ? "en-US" : "de-DE",
                      {
                        // Updated currency formatting
                        style: "currency",
                        currency: currency,
                      },
                    ).format(context.parsed.y);
                  }
                  return label;
                },
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function (value) {
                  return (
                    (currency === "USD" ? "$" : "€") +
                    new Intl.NumberFormat().format(value as number)
                  );
                },
              },
            },
          },
        },
      });
    }

    return () => {
      if (pieChartInstance.current) {
        pieChartInstance.current.destroy();
      }
      if (barChartInstance.current) {
        barChartInstance.current.destroy();
      }
    };
  }, [pieChartData, barChartData, currency, t]);

  return (
    <>
      <Card className="bg-white p-4 border border-neutral-200 shadow-sm">
        <h4 className="font-medium text-center mb-3">
          {t("tools.salary.monthlyBreakdown")}
        </h4>
        <div style={{ height: "300px" }}>
          <canvas ref={pieChartRef} />
        </div>
      </Card>

      <Card className="bg-white p-4 border border-neutral-200 shadow-sm">
        <h4 className="font-medium text-center mb-3">
          {t("tools.salary.annualComparison")}
        </h4>
        <div style={{ height: "300px" }}>
          <canvas ref={barChartRef} />
        </div>
      </Card>
    </>
  );
};

export default SalaryCharts;
